import {
	PropositionStatus,
	PropositionStatusValues,
	RelatedEntityType,
} from "@dune/types";
import { Button } from "@dune/ui";
import {
	getPropositionStatusBadgeClasses,
	getPropositionStatusLabel,
} from "@dune/utils";
import { ArrowLeft } from "@untitled-ui/icons-react";
import { parseAsString, useQueryState } from "nuqs";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUpdateProposition } from "../../api/mutations/propositions.mutations";
import { useImportEmailAttachmentsProposition } from "../../api/mutations/mail.mutations";
import {
	useContract,
	useContractDocuments,
} from "../../api/queries/contracts.queries";
import {
	usePropositionCommunications,
	usePropositionTasks,
	usePropositionUnprocessedCommunicationsCount,
} from "../../api/queries/propositions.queries";
import { useMe } from "../../api/queries/users.queries";
import ConfirmationDialog from "../../component/common/ConfirmationDialog";
import PageLayoutWithTabs, {
	Tab,
	TabLayoutSkeleton,
	TabLayoutErrorSkeleton,
} from "../../component/layout/PageLayoutWithTabs";
import { useEntityPanelSync } from "../../hooks/useEntityPanelSync";
import { usePagination } from "../../hooks/usePagination";
import { useRequiredParams } from "../../hooks/useRequiredParams";
import type { EmailComposerPanelProps } from "../../types/panel.types";
import { PropositionEmailComposerPanel } from "../propositions/components/panels/PropositionEmailComposerPanel";
import PropositionTaskDetailsPanel from "../propositions/components/panels/PropositionTaskDetailsPanel";
import { CommunicationsTab } from "../propositions/components/tabs/CommunicationsTab";
import { DocumentsTab } from "../propositions/components/tabs/DocumentsTab";
import { TasksTab } from "../propositions/components/tabs/TasksTab";
import { GeneralTab } from "./components/tabs/GeneralTab";
import { ReceiptsTab } from "../propositions/components/tabs/ReceiptsTab";
import SendDocumentModal from "../propositions/components/modals/SendDocumentModal";
import { propositionsQueryKeys } from "../../api/query-keys/propositions.query-keys";
import { useQueryClient } from "@tanstack/react-query";
import { RoleEnum } from "../../../../../packages/vars/src";
import { generatePropositionEmailWithFallback } from "../../utils/emails.utils";

const ContractPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useRequiredParams<{ id: string }>();
	const contractId = Number.parseInt(id);
	const { data: contract, isLoading, error } = useContract(contractId);
	const { me } = useMe();
	const isInsurer = me?.role === RoleEnum.INSURER;
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
	const updatePropositionMutation = useUpdateProposition();
	const [isSendContractModalOpen, setIsSendContractModalOpen] = useState(false);
	const [
		isSendEndOfWorkAmendmentModalOpen,
		setIsSendEndOfWorkAmendmentModalOpen,
	] = useState(false);
	const queryClient = useQueryClient();
	const { mutate: importEmailAttachments } =
		useImportEmailAttachmentsProposition(contractId);

	const pagination = usePagination({
		initialLimit: 10,
		initialSortBy: "sentAt",
		initialSortOrder: "desc",
	});

	const {
		data: communicationsResponse,
		isLoading: communicationsLoading,
		error: communicationsError,
	} = usePropositionCommunications(contractId, pagination.params);

	const { data: tasks } = usePropositionTasks(contractId);

	const { data: contractDocuments } = useContractDocuments(contractId);

	const { data: unprocessedCommunicationsCount = 0 } =
		usePropositionUnprocessedCommunicationsCount(contractId);

	const todoTasksCount = useMemo(() => {
		if (!tasks) return 0;
		return tasks.filter((task) => task.status === "TO_DO").length;
	}, [tasks]);

	const toProcessDocumentsCount = useMemo(() => {
		if (!contractDocuments) return 0;
		return contractDocuments.filter(
			(d) => d.file.humanProcessingStatus === "TO_BE_PROCESSED",
		).length;
	}, [contractDocuments]);

	const tabs: Tab[] = useMemo(() => {
		const baseTabs: Tab[] = [
			{ value: "general", label: "Général" },
			{ value: "receipts", label: "Quittances" },
			{
				value: "documents",
				label: "Documents",
				notificationCount: toProcessDocumentsCount,
			},
			{ value: "taches", label: "Tâches", notificationCount: todoTasksCount },
		];

		// Masquer les onglets "Communications" et "Activité" pour les syndics
		if (me?.role !== RoleEnum.SYNDIC) {
			baseTabs.push(
				{
					value: "communications",
					label: "Communications",
					notificationCount: unprocessedCommunicationsCount,
				},
				{ value: "activite", label: "Activité" },
			);
		}

		return baseTabs;
	}, [
		me?.role,
		todoTasksCount,
		unprocessedCommunicationsCount,
		toProcessDocumentsCount,
	]);

	const [activeTab, setActiveTab] = useQueryState(
		"tab",
		parseAsString.withDefault("general"),
	);

	const effectiveActiveTab = activeTab;

	const panelComponents = useMemo(
		() => ({
			TaskDetails: PropositionTaskDetailsPanel,
			EmailComposer: (props: EmailComposerPanelProps) => (
				<PropositionEmailComposerPanel {...props} />
			),
		}),
		[],
	);

	const panelEmailConfig = useMemo(
		() => ({
			getReplyToEmail: (entityId: number, emailRoutingUuid?: string | null) => {
				return generatePropositionEmailWithFallback(emailRoutingUuid, entityId);
			},
			importEmailAttachmentsHandler: (emailAttachmentId: number) => {
				importEmailAttachments({ emailAttachmentId });
			},
		}),
		[importEmailAttachments],
	);

	const panelTasks = useMemo(() => tasks ?? [], [tasks]);

	const tabContent: { [key: string]: React.ReactNode } = useMemo(() => {
		if (!contract) return {};

		const baseContent: { [key: string]: React.ReactNode } = {
			general: <GeneralTab proposition={contract} />,
			receipts: <ReceiptsTab proposition={contract} />,
			documents: <DocumentsTab proposition={contract} />,
			taches: <TasksTab proposition={contract} />,
		};

		if (me?.role !== RoleEnum.SYNDIC) {
			baseContent.communications = (
				<CommunicationsTab
					proposition={contract}
					communicationsResponse={communicationsResponse}
					isLoading={communicationsLoading}
					error={communicationsError}
					onPageChange={pagination.handlePageChange}
					onSortChange={pagination.handleSortChange}
					onLimitChange={pagination.handleLimitChange}
				/>
			);
			baseContent.activite = (
				<div className="p-6">Activité - À implémenter</div>
			);
		}

		return baseContent;
	}, [
		contract,
		me?.role,
		communicationsResponse,
		communicationsLoading,
		communicationsError,
		pagination.handlePageChange,
		pagination.handleSortChange,
		pagination.handleLimitChange,
	]);

	useEntityPanelSync({
		entityType: RelatedEntityType.PROPOSITION,
		entityId: contractId,
		communications: communicationsResponse?.data ?? [],
		tasks: panelTasks,
		emailRoutingUuid: contract?.emailRoutingUuid,
		components: panelComponents,
		emailConfig: panelEmailConfig,
	});

	const displaySignButton = useMemo(() => {
		if (!contract || !tasks || isInsurer) return false;
		const sendContract = tasks.find(
			(task) => task.templateKey === "SEND_CONTRACT",
		);

		return sendContract?.status === "TO_DO";
	}, [contract, tasks, isInsurer]);

	const displaySendEndOfWorkAmendmentButton = useMemo(() => {
		if (!contract || !tasks || isInsurer) return false;
		const sendEndOfWorkAmendment = tasks.find(
			(task) => task.templateKey === "SEND_END_OF_WORK_AMENDMENT",
		);

		return sendEndOfWorkAmendment?.status === "TO_DO";
	}, [contract, tasks, isInsurer]);

	if (isLoading) {
		return <TabLayoutSkeleton tabCount={tabs.length} />;
	}

	if (error || !contract) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des informations du contrat."
				tabCount={tabs.length}
			/>
		);
	}

	const handleCancelContract = async () => {
		try {
			await updatePropositionMutation.mutateAsync({
				id: contractId,
				status: PropositionStatus.SANS_SUITE,
			});
			setIsCancelModalOpen(false);
		} catch (error) {
			console.error("Error updating contract:", error);
		}
	};

	const getStatusBadge = (status: PropositionStatusValues) => {
		const classes = getPropositionStatusBadgeClasses(status);
		const label = getPropositionStatusLabel(status);
		return (
			<div
				className={`inline-flex items-center px-2 py-1 rounded-full text-xs border whitespace-nowrap ${classes}`}
			>
				{label}
			</div>
		);
	};

	return (
		<>
			<PageLayoutWithTabs
				title={
					<div className="flex items-center gap-3">
						{contract.reference}
						{getStatusBadge(contract.status)}
					</div>
				}
				subtitle={
					me?.role === RoleEnum.DUNE && contract.brokerSociety?.denomination
						? `Apporteur: ${contract.brokerSociety.denomination}`
						: undefined
				}
				tabs={tabs}
				activeTab={effectiveActiveTab}
				onTabChange={setActiveTab}
				tabContent={tabContent}
				topActions={
					<div className="flex items-center gap-3 w-full justify-between">
						<Button
							variant="ghost"
							onClick={() => navigate("/contracts")}
							className="has-[>svg]:px-1"
						>
							<ArrowLeft />
							Retour
						</Button>
						{!isInsurer && (
							<div className="flex items-center gap-3">
								{/* <Button
									variant="outline"
									className="text-gray-700"
									onClick={() => setIsCancelModalOpen(true)}
								>
									Résilier le contrat
								</Button> */}
								{displaySignButton && (
									<Button onClick={() => setIsSendContractModalOpen(true)}>
										Signer le contrat
									</Button>
								)}
								{displaySendEndOfWorkAmendmentButton && (
									<Button
										onClick={() => setIsSendEndOfWorkAmendmentModalOpen(true)}
									>
										Signer l'avenant de fin de travaux
									</Button>
								)}
							</div>
						)}
					</div>
				}
			/>
			<ConfirmationDialog
				open={isCancelModalOpen}
				onOpenChange={setIsCancelModalOpen}
				title="Résilier le contrat"
				description="Êtes-vous sûr de vouloir résilier ce contrat ? Cette action ne peut pas être annulée."
				confirmText="Résilier le contrat"
				cancelText="Annuler"
				variant="default"
				onConfirm={handleCancelContract}
				isLoading={updatePropositionMutation.isPending}
			/>
			{isSendContractModalOpen && (
				<SendDocumentModal
					type="CONTRACT"
					open={isSendContractModalOpen}
					onOpenChange={setIsSendContractModalOpen}
					propositionId={contract.id}
					onSuccess={async () => {
						await queryClient.invalidateQueries({
							queryKey: propositionsQueryKeys.tasks(contract.id),
							refetchType: "active",
						});
					}}
				/>
			)}
			{isSendEndOfWorkAmendmentModalOpen && (
				<SendDocumentModal
					type="END_OF_WORK_AMENDMENT"
					open={isSendEndOfWorkAmendmentModalOpen}
					onOpenChange={setIsSendEndOfWorkAmendmentModalOpen}
					propositionId={contract.id}
					onSuccess={async () => {
						await queryClient.invalidateQueries({
							queryKey: propositionsQueryKeys.tasks(contract.id),
							refetchType: "active",
						});
					}}
				/>
			)}
		</>
	);
};

export default ContractPage;
